'use client';
import React, { useState, useRef } from 'react';
import {
  Pencil,
  Search,
  Trash2,
  Save,
  Eye,
  Bold,
  Italic,
  Underline,
  Link,
  List,
  Image as ImageIcon,
  X,
} from 'lucide-react';
export default function TopicsManager() {
  const [view, setView] = useState('list');
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [search, setSearch] = useState('');
  const [body, setBody] = useState(
    `Register and save time!\n\nRegister with us for future convenience:\n\n• Fast and easy checkout\n• Easy access to your order history and status`
  );
  const [showPreview, setShowPreview] = useState(false);
  const editorRef = useRef(null);
  const topics = [
    { id: 1, name: 'ApplyVendor', published: true },
    { id: 2, name: 'CheckoutAsGuestOrRegister', published: true },
    { id: 3, name: 'ContactUs', published: true },
    { id: 4, name: 'ForumWelcomeMessage', published: false },
    { id: 5, name: 'HomepageText', published: false },
    { id: 6, name: 'LoginRegistrationInfo', published: true },
    { id: 7, name: 'PageNotFound', published: true },
    { id: 8, name: 'VendorTermsOfService', published: true },
    { id: 9, name: 'ShippingInfo', published: true },
    { id: 10, name: 'PrivacyInfo', published: true },
    { id: 11, name: 'ConditionsOfUse', published: true },
    { id: 12, name: 'AboutUs', published: true },
  ];
  const handleEdit = (topic) => {
    setSelectedTopic(topic);
    setView('edit');
  };
  const handleSave = () => {
    alert('Saved successfully!');
    setView('list');
  };
  const handleDelete = () => {
    alert('Deleted!');
    setView('list');
  };
  const insertAtCursor = (insertText) => {
    const textarea = editorRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newText =
      textarea.value.substring(0, start) +
      insertText +
      textarea.value.substring(end);
    setBody(newText);
  };
  const applyFormat = (format) => {
    const textarea = editorRef.current;
    const { selectionStart, selectionEnd, value } = textarea;
    const selected = value.substring(selectionStart, selectionEnd);
    let formatted = selected;
    switch (format) {
      case 'bold':
        formatted = `**${selected}**`;
        break;
      case 'italic':
        formatted = `*${selected}*`;
        break;
      case 'underline':
        formatted = `<u>${selected}</u>`;
        break;
      case 'bullet':
        formatted = `• ${selected}`;
        break;
      case 'link': {
        const url = prompt('Enter link URL:', 'https://');
        formatted = `[${selected || 'link text'}](${url})`;
        break;
      }
      case 'image': {
        const url = prompt('Enter image URL:', 'https://example.com/image.jpg');
        if (url) formatted = `![alt text](${url})`;
        break;
      }
      case 'clear':
        formatted = selected.replace(/[*_~`#<>]/g, '');
        break;
      default:
        break;
    }
    const newText =
      value.substring(0, selectionStart) + formatted + value.substring(selectionEnd);
    setBody(newText);
  };
  const renderFormattedBody = () => {
    // Convert markdown-like syntax to HTML (basic)
    let html = body
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/<u>(.*?)<\/u>/g, '<u>$1</u>')
      .replace(/\n/g, '<br>')
      .replace(/•/g, '<li>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" class="text-blue-600 underline">$1</a>')
      .replace(/!\[.*?\]\((.*?)\)/g, '<img src="$1" alt="img" class="max-w-full rounded my-2" />');
    return { __html: html };
  };
  // EDIT VIEW
  if (view === 'edit' && selectedTopic) {
    return (
      <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
        <div className="max-w-5xl mx-auto bg-white shadow-lg rounded-xl p-6 relative">
          {/* Preview Modal */}
          {showPreview && (
            <div className="absolute inset-0 bg-black/60 flex justify-center items-center z-50">
              <div className="bg-white rounded-lg shadow-lg w-11/12 max-w-3xl p-6 relative overflow-y-auto max-h-[80vh]">
                <button
                  onClick={() => setShowPreview(false)}
                  className="absolute top-3 right-3 bg-gray-200 hover:bg-gray-300 p-1 rounded-full"
                >
                  <X size={18} />
                </button>
                <h3 className="text-xl font-semibold mb-4">Preview - {selectedTopic.name}</h3>
                <div
                  className="prose max-w-none text-gray-800"
                  dangerouslySetInnerHTML={renderFormattedBody()}
                />
              </div>
            </div>
          )}
          {/* Header */}
          <div className="flex justify-between items-center border-b pb-3 mb-4">
            <h2 className="text-2xl font-semibold text-gray-800">
              Edit topic details - {selectedTopic.name}
            </h2>
            <button
              onClick={() => setView('list')}
              className="text-blue-600 text-sm underline"
            >
              Back to topic list
            </button>
          </div>
          {/* Info Section */}
          <div className="mb-6">
            <h3 className="font-semibold text-lg mb-2 text-gray-700">Info</h3>
            <div className="space-y-3">
              <div>
                <label className="font-medium text-gray-700 text-sm">Title</label>
                <input
                  type="text"
                  defaultValue={selectedTopic.name}
                  className="w-full mt-1 border rounded-lg p-2 text-sm"
                />
              </div>
              {/* Toolbar */}
              <div className="border rounded-t-lg bg-gray-100 p-2 flex flex-wrap gap-2">
                <button onClick={() => applyFormat('bold')} className="p-1 hover:bg-gray-200 rounded">
                  <Bold size={16} />
                </button>
                <button onClick={() => applyFormat('italic')} className="p-1 hover:bg-gray-200 rounded">
                  <Italic size={16} />
                </button>
                <button onClick={() => applyFormat('underline')} className="p-1 hover:bg-gray-200 rounded">
                  <Underline size={16} />
                </button>
                <button onClick={() => applyFormat('bullet')} className="p-1 hover:bg-gray-200 rounded">
                  <List size={16} />
                </button>
                <button onClick={() => applyFormat('link')} className="p-1 hover:bg-gray-200 rounded">
                  <Link size={16} />
                </button>
                <button onClick={() => applyFormat('image')} className="p-1 hover:bg-gray-200 rounded">
                  <ImageIcon size={16} />
                </button>
                <button onClick={() => applyFormat('clear')} className="p-1 hover:bg-gray-200 rounded text-sm">
                  Clear
                </button>
              </div>
              {/* Body */}
              <textarea
                ref={editorRef}
                className="w-full border border-t-0 rounded-b-lg p-2 text-sm h-48 focus:ring-2 focus:ring-blue-500"
                value={body}
                onChange={(e) => setBody(e.target.value)}
              ></textarea>
              <p className="text-sm text-blue-500">
                URL: https://admin-demo.routekart.com/{selectedTopic.name.toLowerCase()}
              </p>
            </div>
          </div>
          {/* Display Section */}
          <div className="mb-6">
            <h3 className="font-semibold text-lg mb-2 text-gray-700">Display</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                'Published',
                'Include in top menu',
                'Include in footer (column 1)',
                'Include in footer (column 2)',
                'Include in footer (column 3)',
              ].map((label, i) => (
                <label key={i} className="flex items-center space-x-2 text-sm text-gray-700">
                  <input type="checkbox" className="rounded text-blue-600" />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowPreview(true)}
              className="flex items-center gap-2 bg-sky-500 text-white px-4 py-2 rounded-lg hover:bg-sky-600 transition"
            >
              <Eye size={18} /> Preview
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition"
            >
              <Save size={18} /> Save
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
            >
              <Trash2 size={18} /> Delete
            </button>
          </div>
        </div>
      </div>
    );
  }
  // LIST VIEW
  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto bg-white shadow-lg rounded-xl p-6">
        <div className="flex flex-wrap justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold text-gray-800">Topics (pages)</h2>
          <button className="bg-blue-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-blue-700">
            Add New
          </button>
        </div>
        {/* Search */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder="Search keywords..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border p-2 rounded-lg w-full sm:w-1/2 text-sm"
          />
          <button className="flex items-center gap-2 bg-sky-500 text-white px-3 py-2 rounded-lg hover:bg-sky-600">
            <Search size={16} /> Search
          </button>
        </div>
        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-200 text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-center">Published</th>
                <th className="px-4 py-2 text-center">Password protected</th>
                <th className="px-4 py-2 text-center">Include in sitemap</th>
                <th className="px-4 py-2 text-center">Include in top menu</th>
                <th className="px-4 py-2 text-center">Include in footer (1)</th>
                <th className="px-4 py-2 text-center">Display order</th>
                <th className="px-4 py-2 text-center">Edit</th>
              </tr>
            </thead>
            <tbody>
              {topics
                .filter((t) => t.name.toLowerCase().includes(search.toLowerCase()))
                .map((topic) => (
                  <tr key={topic.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-2">{topic.name}</td>
                    <td className="px-4 py-2 text-center">
                      {topic.published ? (
                        <span className="text-green-500 font-bold">✔</span>
                      ) : (
                        <span className="text-red-500 font-bold">✘</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center text-red-500 font-bold">✘</td>
                    <td className="px-4 py-2 text-center text-red-500 font-bold">✘</td>
                    <td className="px-4 py-2 text-center text-red-500 font-bold">✘</td>
                    <td className="px-4 py-2 text-center text-red-500 font-bold">✘</td>
                    <td className="px-4 py-2 text-center">1</td>
                    <td className="px-4 py-2 text-center">
                      <button
                        onClick={() => handleEdit(topic)}
                        className="flex items-center justify-center bg-blue-600 text-white rounded-lg px-3 py-1 hover:bg-blue-700 mx-auto"
                      >
                        <Pencil size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex justify-between items-center mt-4 text-sm">
          <button className="bg-gray-200 px-3 py-1 rounded">Previous</button>
          <span>1-12 of 12 items</span>
          <button className="bg-gray-200 px-3 py-1 rounded">Next</button>
        </div>
      </div>
    </div>
  );
}
